/**
 * Placeholder option data for the /details wizard's image-driven steps.
 * `imageUrl` is intentionally left null until real photography/design assets
 * are supplied — OptionCard/ColorPickerField fall back to a labeled
 * placeholder box when it's missing, so swapping in real URLs here later
 * requires no component changes.
 */

export const PHOTO_LAYOUT_OPTIONS = [
  {
    id: "single",
    code: "A",
    label: "Single photo",
    description: "Each photo on its own page",
    imageUrl:
      "https://assets.mystoriesmatter.com/assets/book-details-single-photo.png",
    showBgImg: false,
  },
  {
    id: "auto-collage",
    code: "B",
    label: "Auto-collage",
    description: "Group the photos together per chapter",
    imageUrl:
      "https://assets.mystoriesmatter.com/assets/book-details-auto-collage.png",
    showBgImg: false,
  },
  {
    id: "inline",
    code: "C",
    label: "In-between paragraphs",
    description:
      "Photos placed in the text, in-between two paragraphs (Additional costs may apply)",
    imageUrl:
      "https://assets.mystoriesmatter.com/assets/book-details-inline.png",
    showBgImg: false,
  },
  {
    id: "text-wrap",
    code: "C",
    label: "Text wrap",
    description: "Place photos within the text based on the chapter's content.",
    imageUrl:
      "https://assets.mystoriesmatter.com/assets/book-details-text-wrap.png ",
    showBgImg: false,
  },
  {
    id: "no-image",
    code: "D",
    label: "Not applicable",
    description:
      "N/A: Not applicable since I do not have photos included inside my book",
    imageUrl:
      "https://assets.mystoriesmatter.com/assets/book-details-no-image.svg",
    showBgImg: false,
  },
];

export const ADD_ON_OPTIONS = [
  {
    id: "photo-restoration",
    code: "A",
    label: "Photo restoration and enhancement",
    description:
      "Starting at $10 USD per photo, price varies based on the requested edits",
    imageUrl:
      "https://assets.mystoriesmatter.com/assets/book-details-add-ons-photo-restoration.png",
    showBgImg: false,
  },
  {
    id: "proofreading",
    code: "B",
    label: "Proofreading and editing",
    description:
      "Spelling, grammar, story consistency, improved readability, and more",
    imageUrl:
      "https://assets.mystoriesmatter.com/assets/book-details-add-ons-proof-reading.png",
    showBgImg: false,
  },
  {
    id: "custom-family-tree",
    code: "C",
    label: "Custom family tree",
    description: "Price varies based on request",
    imageUrl:
      "https://assets.mystoriesmatter.com/assets/book-details-add-ons-family-tree.png",
    showBgImg: false,
  },
  {
    id: "special-formatting",
    code: "D",
    label: "Special formatting request",
    description: "Price varies based on requirements",
    imageUrl:
      "https://assets.mystoriesmatter.com/assets/book-details-add-ons-extra-formatting.png",
    showBgImg: false,
  },
  {
    id: "none",
    code: "E",
    label: "None of the above",
    imageUrl: null,
    showBgImg: false,
  },
];

export const FRONT_COVER_STYLE_OPTIONS = [
  {
    id: "a",
    code: "A",
    label: "Option 1",
    imageUrl:
      "https://assets.mystoriesmatter.com/assets/front-cover-option-1.png",
    showBgImg: false,
  },
  {
    id: "b",
    code: "B",
    label: "Option 2",
    imageUrl:
      "https://assets.mystoriesmatter.com/assets/front-cover-option-2.png",
    showBgImg: false,
  },
  {
    id: "e",
    code: "C",
    label: "Option 3",
    imageUrl:
      "https://assets.mystoriesmatter.com/assets/front-cover-option-3.png",
    showBgImg: false,
  },
  {
    id: "c",
    code: "D",
    label: "Option 4",
    imageUrl:
      "https://assets.mystoriesmatter.com/assets/front-cover-option-4.png",
    showBgImg: false,
  },
  {
    id: "d",
    code: "E",
    label: "Option 5",
    imageUrl:
      "https://assets.mystoriesmatter.com/assets/front-cover-option-5.png",
    showBgImg: false,
  },
  {
    id: "custom",
    code: "F",
    label: "Custom design",
    description:
      "I have a specific design request (Our designer will reach out to you; additional costs may apply)",
    imageUrl:
      "https://assets.mystoriesmatter.com/assets/front-cover-option-6.png",
    showBgImg: false,
  },
];

export const BACK_COVER_STYLE_OPTIONS = [
  {
    id: "color",
    code: "A",
    label: "Solid color only",
    imageUrl:
      "https://assets.mystoriesmatter.com/assets/back-cover-option-1.png",
    showBgImg: false,
  },
  {
    id: "image-only",
    code: "B",
    label: "Full-cover photo",
    imageUrl:
      "https://assets.mystoriesmatter.com/assets/back-cover-option-2.png",
    showBgImg: false,
  },
  {
    id: "author-bio",
    code: "C",
    label: "Quote with summary",
    description: "We will reach out to you for content",
    imageUrl:
      "https://assets.mystoriesmatter.com/assets/back-cover-option-3.png",
    showBgImg: false,
  },
  {
    id: "custom",
    code: "D",
    label: "Custom design",
    description:
      "I have a specific design request (Designer will reach out to you; additional costs may apply)",
    imageUrl:
      "https://assets.mystoriesmatter.com/assets/back-cover-option-4.png",
    showBgImg: false,
  },
];

// All cover colors are swatched on a single reference image (printed names,
// not per-color crops), so ColorPickerField shows this same image regardless
// of which option is selected.
export const COLOR_SWATCH_IMAGE_URL =
  "https://assets.mystoriesmatter.com/assets/book-details-color-swatch.png";

export const getOptionLabel = (options, id) =>
  options?.find((o) => o.id === id)?.label ?? null;

export const getOptionImage = (options, id) =>
  options?.find((o) => o.id === id)?.imageUrl ?? null;

export const getOptionHex = (options, id) =>
  options?.find((o) => o.id === id)?.hex ?? null;

export const COVER_COLOR_PALETTE = [
  { id: "spun-sugar", label: "Spun Sugar", hex: "#98D8E5" },
  { id: "bubblegum", label: "Bubblegum", hex: "#F9BBBB" },
  { id: "fuschia", label: "Fuschia", hex: "#D52F66" },
  { id: "skydiver", label: "Skydiver", hex: "#0062A4" },
  { id: "daffodil", label: "Daffodil", hex: "#FFC42F" },
  { id: "harbor-blue", label: "Harbor Blue", hex: "#007681" },
  { id: "mocha", label: "Mocha", hex: "#91715D" },
  { id: "dahlia", label: "Dahlia", hex: "#944A88" },
  { id: "scarlet", label: "Scarlet", hex: "#DA322A" },
  { id: "snow-white", label: "Snow White", hex: "#F2EDE6" },
  { id: "basil", label: "Basil", hex: "#7C9F84" },
  { id: "koala", label: "Koala", hex: "#BCC1C0" },
];
